import * as Sentry from '@sentry/react';
import moment from 'moment';
import { darken } from 'polished';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import outlookIcon from '~/assets/public/outlook-icon.svg';
import { Button, Dropdown, Duration, FormMessage, Icon, Spinner, Tooltip } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { colors, weights } from '~/styles';
import { dateFormats } from '~/utils';

const Row = styled.div`
  padding: 0.75rem 0;

  .calendar-events-menu {
    right: 0;
  }
`;

const Control = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 2.5rem;
  color: ${colors.black};
  background-color: ${colors.white};
  border: solid 1px ${({ isOpen }) => (isOpen ? colors.primary : colors.grey10)};
  border-radius: 0.3125rem;
  cursor: pointer;
  user-select: none;
`;

const FloatingLabel = styled.div`
  position: absolute;
  top: 0;
  left: 0.625rem;
  margin-left: 1px;
  padding: 0 0.25rem;
  color: ${colors.grey40};
  font-size: 0.75rem;
  background-color: ${colors.white};
  border-radius: 0.3125rem;
  transform: translateY(-50%);
  opacity: ${({ isVisible }) => (isVisible ? '1' : '0')};
  transition: opacity 100ms;
  pointer-events: none;
`;

const Display = styled.div`
  flex: 1;
  padding: 0 0 0 1rem;
  color: ${colors.black};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const Placeholder = styled.span`
  color: ${colors.grey40};
`;

const Indicator = styled.div`
  flex-shrink: 0;
  color: ${colors.grey25};
  padding: 0 0.9rem;
`;

const ClearButton = styled.button`
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding-left: 1rem;
  padding-right: 1.05rem;
  background: transparent;
  border: none;
  border-right: 1px solid ${colors.grey5};
  border-radius: 0;
  color: ${colors.primary};
  font-size: 0.875rem;
  font-weight: ${weights.normal};

  &:hover {
    color: ${darken(0.1, colors.primary)};
    background: transparent;
  }
`;

const Logo = styled.img`
  width: 1rem;
  height: 1rem;
  margin-top: 0.125rem;
  flex-shrink: 0;
`;

const EventCard = styled(Button)`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 0.625rem;
  width: 100%;
  padding: 0.625rem 0.875rem;
  border-radius: 0;
  text-align: left;

  &:hover {
    background: ${colors.grey5};
  }
`;

const EventBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const OptionIndicator = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  width: 0.75rem;
  margin-top: 0.125rem;
  color: ${colors.grey40};
  font-size: 0.75rem;
`;

const EventName = styled.div`
  font-size: 0.875rem;
  font-weight: ${weights.bold};
  color: ${colors.black};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const EventMeta = styled.div`
  margin-top: 0.125rem;
  font-size: 0.75rem;
  color: ${colors.grey55};
`;

const RecurrenceIcon = styled(Icon)`
  margin-left: 0.375rem;
  font-size: 0.625rem;
  color: ${colors.grey40};
`;

const EmptyMessage = styled.div`
  padding: 0.5rem 0.875rem;
  font-size: 0.8125rem;
  color: ${colors.grey55};
`;

const EventList = styled.div`
  max-height: 15rem;
  overflow-y: auto;
`;

const LinkedEventCard = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 0.625rem;
  width: 100%;
  padding: 0.625rem 0.875rem;
  text-align: left;
  cursor: default;
  opacity: 0.5;
`;

function timeLabel(value) {
  return moment(value, dateFormats.shortTime).format('h:mma');
}

function whenLabel(event) {
  if (event.isAllDay) return event.type === 'time_off' ? 'All day · Time Off' : 'All day';
  const range = `${timeLabel(event.startTime)} – ${timeLabel(event.endTime)}`;
  return event.type === 'time_off' ? `${range} · Time Off` : range;
}

function reasonMessage(reason) {
  switch (reason) {
    case 'throttled':
      return 'Outlook is limiting how often we can read your calendar. Please try again in a moment.';
    case 'auth_failed':
      return 'Your Outlook connection has expired. Reconnect your calendar in My Profile → Calendar to see your events here.';
    case 'consent_required':
      return 'Outlook no longer lets Ruddr read your calendar. Reconnect your calendar in My Profile → Calendar to see your events here.';
    case 'impersonating':
      return 'The calendar is not available while impersonating a member.';
    case 'graph_error':
      return 'We could not reach Outlook right now. Please try again in a moment.';
    default:
      return null;
  }
}

function EventOption({ event, isSelected, onSelect }) {
  const content = (
    <>
      <OptionIndicator>{isSelected && <Icon icon="check" />}</OptionIndicator>
      <Logo src={outlookIcon} alt="" />
      <EventBody>
        <EventName>{event.subject}</EventName>
        <EventMeta>
          {whenLabel(event)}
          {event.microsoftSeriesId && (
            <RecurrenceIcon icon="repeat" title="Recurring event" data-testid="recurrence-icon" />
          )}
        </EventMeta>
        <EventMeta>
          <Duration minutes={event.minutes} /> hrs
        </EventMeta>
      </EventBody>
    </>
  );

  if (event.linked)
    return (
      <Tooltip message="You have already logged time for this event." delay={500}>
        <LinkedEventCard tabIndex={0} aria-disabled="true">
          {content}
        </LinkedEventCard>
      </Tooltip>
    );

  return (
    <EventCard isAnchor onClick={() => onSelect(event)}>
      {content}
    </EventCard>
  );
}

function EventsMenu({ isLoading, reason, events, selectedEvent, onSelect }) {
  if (isLoading)
    return (
      <EmptyMessage data-testid="calendar-events-loading">
        <Spinner />
        Loading events...
      </EmptyMessage>
    );

  const message = reasonMessage(reason);
  if (message) return <EmptyMessage data-testid="calendar-events-error">{message}</EmptyMessage>;

  if (events.length === 0) return <EmptyMessage>No events for this day.</EmptyMessage>;

  return (
    <EventList>
      {events.map((event) => (
        <EventOption key={event.id} event={event} isSelected={selectedEvent?.id === event.id} onSelect={onSelect} />
      ))}
    </EventList>
  );
}

export default function MicrosoftCalendarEventPicker({
  date,
  timezone,
  canLogTimeOff,
  selectedEventId,
  excludeTimeEntryId,
  onSelect,
  onClear,
  setFieldValue,
}) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [isOpen, setIsOpen] = useState(false);
  const [integration, setIntegration] = useState(null);
  const [events, setEvents] = useState([]);
  const [reason, setReason] = useState(null);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [pickError, setPickError] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const loadedDate = useRef(date);
  const loadedKey = useRef(null);
  const requestId = useRef(0);

  useEffect(() => {
    const previousDate = loadedDate.current;
    if (previousDate === date) return;
    loadedDate.current = date;
    if (!previousDate) return;
    loadedKey.current = null;
    setEvents([]);
    setSelectedEvent(null);
    setFieldValue('microsoftEventId', '');
    setFieldValue('microsoftSeriesId', '');
  }, [date, setFieldValue]);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        const { data } = await api.www.workspaces(workspace.id).microsoftCalendar.getIntegration();
        if (!cancelled) setIntegration(data ?? null);
      } catch (error) {
        if (!cancelled) {
          Sentry.captureException(error);
          setIntegration(null);
        }
      } finally {
        if (!cancelled) setIsReady(true);
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [api, workspace.id]);

  const canLoadEvents =
    !!date && !!integration?.isWorkspaceConnected && !!integration?.isConnected && !integration?.isImpersonating;
  const eventsKey = `${date}|${timezone}|${excludeTimeEntryId ?? ''}`;

  const fetchEvents = useCallback(
    async (key) => {
      loadedKey.current = key;
      const id = ++requestId.current;

      setIsLoadingEvents(true);
      setReason(null);
      try {
        const { data } = await api.www.workspaces(workspace.id).microsoftCalendar.getEvents({
          start: date,
          end: date,
          timezone,
          excludeTimeEntryId,
        });
        if (requestId.current !== id) return;

        const list = data?.events ?? [];
        setEvents(list);
        setReason(data?.reason ?? null);
        if (selectedEventId) setSelectedEvent(list.find((event) => event.id === selectedEventId) ?? null);
      } catch (error) {
        if (requestId.current !== id) return;
        Sentry.captureException(error);
        loadedKey.current = null;
        setEvents([]);
        setReason(error?.status === 429 || error?.response?.data?.retryAfter ? 'throttled' : 'graph_error');
      } finally {
        if (requestId.current === id) setIsLoadingEvents(false);
      }
    },
    [api, workspace.id, date, timezone, excludeTimeEntryId, selectedEventId],
  );

  useEffect(() => {
    if (!isReady || !canLoadEvents) return;
    if (!isOpen && !selectedEventId) return;
    if (loadedKey.current === eventsKey) return;

    fetchEvents(eventsKey);
  }, [isReady, canLoadEvents, isOpen, selectedEventId, eventsKey, fetchEvents]);

  const handleClear = () => {
    setSelectedEvent(null);
    onClear();
  };

  const handleToggle = () => {
    if (!isOpen && canLoadEvents && loadedKey.current !== eventsKey) setIsLoadingEvents(true);
    setIsOpen(!isOpen);
  };

  if (!isReady) return null;

  // A member who never connected has nothing to reconnect, so only a lost connection keeps the menu.
  const canPick = integration?.isWorkspaceConnected && (integration.isConnected || integration.needsReconnect);

  const isEventGone = !selectedEvent && loadedKey.current === eventsKey && !isLoadingEvents && reason === null;

  // When the linked event cannot be read, the entry's existing link can still be cleared.
  if (integration?.isImpersonating || !canPick || (selectedEventId && (!integration.isConnected || isEventGone))) {
    if (!selectedEventId) return null;
    return (
      <Row data-testid="calendar-event-picker">
        <Control>
          <FloatingLabel isVisible data-testid="calendar-event-label">
            Calendar Event
          </FloatingLabel>
          <Display data-testid="calendar-event-detached">Linked to an Outlook event</Display>
          {!integration?.isImpersonating && (
            <ClearButton type="button" data-testid="calendar-event-clear" title="Clear event" onClick={handleClear}>
              <Icon icon="times" />
            </ClearButton>
          )}
        </Control>
      </Row>
    );
  }

  const connectionReason = integration.isImpersonating
    ? 'impersonating'
    : integration.isConnected
      ? null
      : 'auth_failed';

  return (
    <Row data-testid="calendar-event-picker">
      <Dropdown position="bottom" isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <Dropdown.Trigger>
          <Control
            isOpen={isOpen}
            role="button"
            tabIndex={0}
            onClick={handleToggle}
            onKeyDown={(e) => {
              if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
              e.preventDefault();
              handleToggle();
            }}>
            <FloatingLabel isVisible={!!selectedEvent} data-testid="calendar-event-label">
              Calendar Event
            </FloatingLabel>
            <Display>
              {selectedEvent ? (
                selectedEvent.subject
              ) : (
                <Placeholder data-testid="calendar-event-placeholder">Calendar Event</Placeholder>
              )}
            </Display>
            {(selectedEvent || selectedEventId) && (
              <ClearButton
                type="button"
                data-testid="calendar-event-clear"
                title="Clear event"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}>
                <Icon icon="times" />
              </ClearButton>
            )}
            <Indicator>
              <Icon icon="angle-down" />
            </Indicator>
          </Control>
        </Dropdown.Trigger>
        <Dropdown.Menu className="calendar-events-menu">
          <EventsMenu
            isLoading={isLoadingEvents}
            reason={connectionReason ?? reason}
            events={events}
            selectedEvent={selectedEvent}
            onSelect={(event) => {
              setIsOpen(false);
              if (event.type === 'time_off' && !canLogTimeOff) {
                setPickError('You are not allowed to track time off.');
                return;
              }
              setPickError(null);
              setSelectedEvent(event);
              onSelect(event);
            }}
          />
        </Dropdown.Menu>
      </Dropdown>
      {pickError && (
        <FormMessage.Error spaceTop data-testid="calendar-event-error">
          {pickError}
        </FormMessage.Error>
      )}
    </Row>
  );
}
